import { useEffect, useMemo, useRef, useState } from "react";
import type { MeResponse, SearchResponse, SearchResult } from "../api-models";
import { api } from "./api";
import { modulePath, visibleItems, visibleModules } from "./nav";
import { hasAnyGrant } from "./permissions";
import { REPORT_CATALOG } from "../pages/reports/reportDefs";
import { kindLabel } from "./searchTargets";

/**
 * Global search runs in two tiers, which is what makes the palette feel instant.
 *
 * Pages and reports are matched here, in memory, off the same NAV_MODULES and REPORT_DEFS
 * the landing page renders from — so something appears on the very first keystroke, before
 * any request has left the browser. Documents and masters come from /search, debounced,
 * and drop in underneath a moment later.
 */

/** How long typing has to settle before a request goes out. */
const DEBOUNCE_MS = 150;

/** Matches the server's floor — below this it answers empty, so don't ask. */
const MIN_TERM = 2;

const LOCAL_LIMIT = 5;

/** A page or report: no id, no server round trip, just somewhere to go. */
export interface LocalHit {
  key: string;
  title: string;
  subtitle?: string;
  path: string;
}

export interface HitGroup {
  key: string;
  label: string;
  hits: PaletteHit[];
}

export type PaletteHit =
  | { source: "local"; key: string; title: string; subtitle?: string; path: string }
  | { source: "server"; key: string; hit: SearchResult };

function matches(term: string, ...fields: (string | undefined)[]): boolean {
  const needle = term.toLowerCase();
  return fields.some((f) => f?.toLowerCase().includes(needle));
}

/** Every nav destination the user can reach, flattened once per user rather than per keystroke. */
function navHaystack(user: MeResponse | null): LocalHit[] {
  const out: LocalHit[] = [];
  for (const mod of visibleModules(user)) {
    const items = visibleItems(mod, user);
    // A hub is only worth offering when it is a page in its own right; otherwise its items
    // are the real destinations and listing both just doubles every result.
    if (mod.items.length === 0) {
      out.push({ key: `mod:${mod.key}`, title: mod.label, subtitle: mod.description, path: modulePath(mod) });
    }
    for (const item of items) {
      out.push({ key: `item:${item.key}`, title: item.label, subtitle: mod.label, path: item.path });
    }
  }
  return out;
}

function reportHaystack(user: MeResponse | null): LocalHit[] {
  if (!hasAnyGrant(user, "Reports.Read")) return [];
  // The full catalogue, so the five reports with their own interactive pages are as
  // findable as the generic ones — the hub reads the same list.
  return REPORT_CATALOG.map((report) => ({
    key: `report:${report.key}`,
    title: report.title,
    subtitle: report.desc,
    path: report.path,
  }));
}

export interface GlobalSearchState {
  term: string;
  setTerm: (next: string) => void;
  groups: HitGroup[];
  /** Flat, in display order — what the arrow keys walk. */
  flat: PaletteHit[];
  loading: boolean;
  truncated: boolean;
  /** Nothing matched the term as typed; these are near-misses on name. */
  fuzzy: boolean;
}

export function useGlobalSearch(user: MeResponse | null, perKind?: number): GlobalSearchState {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [fuzzy, setFuzzy] = useState(false);
  const [loading, setLoading] = useState(false);

  const nav = useMemo(() => navHaystack(user), [user]);
  const reports = useMemo(() => reportHaystack(user), [user]);

  // One controller per in-flight request. Aborting the previous one is what keeps a slow
  // answer to "ce" from landing on top of a fast answer to "cement" — the same hazard
  // usePaginatedList's sequence guard exists for.
  const inFlight = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = term.trim();
    inFlight.current?.abort();

    if (trimmed.length < MIN_TERM) {
      setResults([]);
      setTruncated(false);
      setFuzzy(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      const controller = new AbortController();
      inFlight.current = controller;
      try {
        const res = await api.get<SearchResponse>("/search", {
          params: { q: trimmed, ...(perKind ? { limit: perKind } : {}) },
          signal: controller.signal,
        });
        setResults(res.data.results ?? []);
        setTruncated(res.data.truncated);
        setFuzzy(res.data.fuzzy);
        setLoading(false);
      } catch {
        // An abort is the ordinary case here — the user typed another character. Either way
        // the palette shows what it has rather than an error; the local tier still works.
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, perKind]);

  const groups = useMemo<HitGroup[]>(() => {
    const trimmed = term.trim();
    if (!trimmed) return [];

    const out: HitGroup[] = [];
    const local = (key: string, label: string, pool: LocalHit[]) => {
      const hits = pool
        .filter((h) => matches(trimmed, h.title, h.subtitle))
        .slice(0, perKind ?? LOCAL_LIMIT)
        .map<PaletteHit>((h) => ({ source: "local", key: h.key, title: h.title, subtitle: h.subtitle, path: h.path }));
      if (hits.length > 0) out.push({ key, label, hits });
    };

    // Pages first: they are the cheapest thing to be looking for, and the only group that
    // is already correct while the request is still out.
    local("pages", "Pages", nav);
    local("reports", "Reports", reports);

    // Server results arrive flat and globally ranked; grouping preserves that order within
    // each group, so the best hit overall is still the first row of its section.
    const byKind = new Map<string, PaletteHit[]>();
    for (const hit of results) {
      const bucket = byKind.get(hit.kind) ?? [];
      bucket.push({ source: "server", key: `${hit.kind}:${hit.id}`, hit });
      byKind.set(hit.kind, bucket);
    }
    for (const [kind, hits] of byKind) {
      out.push({ key: kind, label: kindLabel(kind), hits });
    }
    return out;
  }, [term, nav, reports, results, perKind]);

  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);

  return { term, setTerm, groups, flat, loading, truncated, fuzzy };
}
